// src/contexts/RegistrationContext.js
import {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useRef,
} from "react";
import dayjs from "dayjs";

const STORAGE_KEY_PREFIX = "registrationData";

const RegistrationContext = createContext();

const createInitialState = () => ({
  role: null,
  currentStep: 0,
  completedSteps: [],
  visitedSteps: [0],
  formData: {
    email: "",
    firstName: "",
    lastName: "",
    username: "",
    pronouns: "",
    dateOfBirth: "",
    invitationCode: "",
    disabilities: "no",
    disabilitiesDescription: "",
    gender: "",
    otherTerm: "",
    race: "",
    disabilityStatus: "",
    title: "",
    countryFrom: null,
    howYouHeardOfUs: "",
    hearFromUsDescription: "",
    bio: "",
    disability: "",
    disabilitySupport: "",
    profilePicture: null,
    teachingLocation1: "",
    teachingLocation2: "",
    institutionType: "",
    institutionName: "",
    yearsOfExperience: "",
    professionalExpertise: "",
    subjects: [],
    otherSubjectName: "",
    institutionTypeOther: "",
    institutionContactNumber: "",
    organization: "",
    organizationSelection: null,
    organisationName: "",
    organisationLevel: "",
    organisationContact: "",
    // Phone numbers are out of scope for this launch.
    // workMobileNumber: "",
    organisationContactNumber: "",
    detailedRole: "",
    coordinatorRole: "",
    mentorRole: "",
    jobTitle: "",
    referralSources: [],
    contactNumber: "",
    contactDialCode: "+44",
    // Phone verification is out of scope for this launch.
    // phoneVerified: false,
  },
});

const storageAvailable =
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const loadState = (storageKey) => {
  const fallback = createInitialState();
  if (!storageKey || !storageAvailable) {
    return fallback;
  }
  try {
    const serializedState = sessionStorage.getItem(storageKey);
    if (!serializedState) {
      return fallback;
    }
    const parsedState = JSON.parse(serializedState);
    if (parsedState.formData?.dateOfBirth) {
      parsedState.formData.dateOfBirth = dayjs(
        parsedState.formData.dateOfBirth,
      );
    }
    return {
      ...fallback,
      ...parsedState,
      visitedSteps: [
        ...new Set([
          0,
          parsedState.currentStep || 0,
          ...(parsedState.completedSteps || []),
          ...(parsedState.visitedSteps || []),
        ]),
      ],
      formData: {
        ...fallback.formData,
        ...(parsedState.formData || {}),
      },
    };
  } catch (err) {
    console.error("Failed to load state from sessionStorage:", err);
    return fallback;
  }
};

const saveState = (storageKey, state) => {
  if (!storageKey || !storageAvailable) {
    return;
  }
  try {
    const dob = state.formData?.dateOfBirth
      ? dayjs(state.formData.dateOfBirth).toISOString()
      : null;
    const stateToSave = {
      ...state,
      formData: {
        ...state.formData,
        dateOfBirth: dob,
      },
    };
    sessionStorage.setItem(storageKey, JSON.stringify(stateToSave));
  } catch (err) {
    console.error("Failed to save state to sessionStorage:", err);
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_ROLE":
      return {
        ...state,
        role: action.payload,
        currentStep: 0,
        completedSteps: [],
        visitedSteps: [0],
      };
    case "SET_STEP":
      return {
        ...state,
        currentStep: action.payload,
        visitedSteps: [
          ...new Set([...(state.visitedSteps || [0]), action.payload]),
        ],
      };
    case "COMPLETE_STEP":
      return {
        ...state,
        completedSteps: [...new Set([...state.completedSteps, action.payload])],
      };
    case "UNCOMPLETE_STEP":
      return {
        ...state,
        completedSteps: state.completedSteps.filter(
          (stepIndex) => stepIndex !== action.payload,
        ),
      };
    case "UPDATE_FORM_DATA":
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload,
        },
      };
    case "RESET_FORM":
      return {
        ...createInitialState(),
        role: state.role,
      };
    case "HYDRATE_STATE": {
      const payload = action.payload || createInitialState();
      const base = createInitialState();
      return {
        ...base,
        ...payload,
        visitedSteps: [
          ...new Set([
            0,
            payload.currentStep || 0,
            ...(payload.completedSteps || []),
            ...(payload.visitedSteps || []),
          ]),
        ],
        formData: {
          ...base.formData,
          ...(payload.formData || {}),
        },
      };
    }
    default:
      return state;
  }
};

export const RegistrationProvider = ({
  children,
  storageKey: storageKeyProp,
  shouldReset,
}) => {
  const resolvedKey = storageKeyProp
    ? `${STORAGE_KEY_PREFIX}_${storageKeyProp}`
    : `${STORAGE_KEY_PREFIX}_guest`;
  const [state, dispatch] = useReducer(reducer, undefined, () =>
    loadState(resolvedKey),
  );
  const previousKeyRef = useRef(resolvedKey);
  const resetRef = useRef(false);

  useEffect(() => {
    if (previousKeyRef.current !== resolvedKey) {
      previousKeyRef.current = resolvedKey;
      dispatch({ type: "HYDRATE_STATE", payload: loadState(resolvedKey) });
    }
  }, [resolvedKey]);

  useEffect(() => {
    saveState(resolvedKey, state);
  }, [state, resolvedKey]);

  useEffect(() => {
    if (shouldReset && !resetRef.current) {
      resetRef.current = true;
      if (storageAvailable) {
        try {
          sessionStorage.removeItem(resolvedKey);
        } catch (err) {
          console.error("Failed to clear registration state:", err);
        }
      }
      dispatch({ type: "RESET_FORM" });
    } else if (!shouldReset && resetRef.current) {
      resetRef.current = false;
    }
  }, [shouldReset, resolvedKey]);

  return (
    <RegistrationContext.Provider value={{ state, dispatch }}>
      {children}
    </RegistrationContext.Provider>
  );
};

export const useRegistration = () => {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error(
      "useRegistration must be used within a RegistrationProvider",
    );
  }
  return context;
};
